import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { candidateEnv } from '@/config/env';

/**
 * Human-readable PT-BR label per top-level route segment. Keys are the first
 * path segment (e.g. `/vagas/123` → `vagas`). A segment without an entry falls
 * back to the bare brand title.
 */
const SECTION_LABELS: Record<string, string> = {
  oportunidades: 'Oportunidades',
  vagas: 'Vagas',
  programas: 'Programas',
  perfil: 'Perfil',
  modulos: 'Etapas de seleção',
  onboarding: 'Onboarding',
  'boas-vindas': 'Boas-vindas',
  'conta-excluida': 'Conta excluída',
  'politica-de-privacidade': 'Política de privacidade',
  'termos-de-uso': 'Termos de uso',
  users: 'Inscrição',
  subscription: 'Inscrição',
};

function sectionLabelForPath(pathname: string): string | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return null;
  return SECTION_LABELS[segment] ?? null;
}

/**
 * Título da aba para um `pathname`: `"<section> · <App>"`, ou só o nome do app
 * na raiz / paths sem mapeamento. Função PURA — fonte única usada pelo
 * `document.title` E pelo `virtual_pageview` (`use-datalayer-pageview`), para
 * que `page_path` e `page_title` derivem do MESMO pathname e nunca dessincronizem.
 */
export function documentTitleForPath(pathname: string): string {
  const label = sectionLabelForPath(pathname);
  return label ? `${label} · ${candidateEnv.appDisplayName}` : candidateEnv.appDisplayName;
}

/**
 * Keeps `document.title` in sync with the current route on every navigation:
 * `"<section> · <App>"`, or just the app name on the root / unmapped paths.
 */
export function useDocumentTitle(): void {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    document.title = documentTitleForPath(pathname);
  }, [pathname]);
}
